import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Activity, ArrowUpRight, Instagram, MessageCircle, Share2, Trophy, Users } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAndroid, faApple } from '@fortawesome/free-brands-svg-icons';
import { supabase } from '../supabaseClient';
import logo from '../assets/logo.png';
import './PublicCommunityInvite.css';

const APP_STORE_URL = 'https://apps.apple.com/ie/app/snows-proam/id6504996530';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.snowsproam.snowsproam';
const DOWNLOAD_URL = 'https://linktr.ee/snowsproam';
const COUNTRY_CODES = {
  argentina: 'AR', australia: 'AU', belgium: 'BE', brazil: 'BR', canada: 'CA', china: 'CN', denmark: 'DK',
  finland: 'FI', france: 'FR', germany: 'DE', india: 'IN', ireland: 'IE', italy: 'IT', japan: 'JP', mexico: 'MX',
  morocco: 'MA', netherlands: 'NL', 'new zealand': 'NZ', nigeria: 'NG', norway: 'NO', pakistan: 'PK',
  philippines: 'PH', poland: 'PL', portugal: 'PT', romania: 'RO', singapore: 'SG', 'south africa': 'ZA',
  'south korea': 'KR', spain: 'ES', sweden: 'SE', switzerland: 'CH', turkey: 'TR',
  'united arab emirates': 'AE', 'united kingdom': 'GB', 'united states': 'US', usa: 'US',
};
const countryFlag = country => {
  const name = String(country || '').trim();
  const code = /^[A-Za-z]{2}$/.test(name) ? name.toUpperCase() : COUNTRY_CODES[name.toLowerCase()];
  return /^[A-Z]{2}$/.test(code || '') ? code.split('').map(letter => String.fromCodePoint(127397 + letter.charCodeAt(0))).join('') : '🌍';
};
const SPORT_EMOJI = { Basketball: '🏀', Football: '⚽', Fitness: '🏃', Track: '🏃‍♂️', Swimming: '🏊', Boxing: '🥊', Badminton: '🏸', Padel: '🎾', Tennis: '🎾', Volleyball: '🏐' };
const sportEmoji = sport => SPORT_EMOJI[String(sport || '').trim()] || '🏅';

export default function PublicCommunityInvite() {
  const { communitySlug = '' } = useParams();
  let decodedCommunitySlug = '';
  try { decodedCommunitySlug = decodeURIComponent(communitySlug).toLowerCase(); } catch { decodedCommunitySlug = ''; }
  const validCommunitySlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(decodedCommunitySlug) ? decodedCommunitySlug : '';
  const [community, setCommunity] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [attempt, setAttempt] = React.useState(0);
  const [copyStatus, setCopyStatus] = React.useState('');
  const fallbackTimer = React.useRef(null);
  const fallbackCleanup = React.useRef(null);

  React.useEffect(() => {
    let active = true;
    setLoading(true); setError(''); setCommunity(null);
    (async () => {
      if (!validCommunitySlug) { setError('This community link is invalid.'); setLoading(false); return; }
      try {
        const { data: previewRows, error: queryError } = await supabase.rpc('get_public_community_share_preview_by_slug', { p_community_slug: validCommunitySlug });
        if (queryError) throw queryError;
        const data = previewRows?.[0] || null;
        if (!active) return;
        if (!data) setError('This community is no longer available.');
        else setCommunity(data);
      } catch { if (active) setError('We could not load this community. Check your connection and try again.'); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [attempt, validCommunitySlug]);

  React.useEffect(() => {
    const previous = document.title;
    document.title = community ? `Join ${community.name} · Snows ProAm` : 'Community invite · Snows ProAm';
    return () => { document.title = previous; };
  }, [community]);

  React.useEffect(() => () => {
    window.clearTimeout(fallbackTimer.current);
    fallbackCleanup.current?.();
  }, []);

  const appLink = community ? `snowsproam://community/${encodeURIComponent(validCommunitySlug)}` : '';
  const isPrivate = Boolean(community?.private || community?.institute_id);

  const openApp = React.useCallback(event => {
    event.preventDefault();
    if (!appLink) return;
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isMobile) { window.location.assign(DOWNLOAD_URL); return; }
    window.clearTimeout(fallbackTimer.current);
    fallbackCleanup.current?.();
    let leftBrowser = false;
    const cleanup = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', cancelFallback);
      window.removeEventListener('blur', cancelFallback);
      fallbackCleanup.current = null;
    };
    const cancelFallback = () => { leftBrowser = true; window.clearTimeout(fallbackTimer.current); cleanup(); };
    const handleVisibilityChange = () => { if (document.visibilityState === 'hidden') cancelFallback(); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', cancelFallback, { once: true });
    window.addEventListener('blur', cancelFallback, { once: true });
    fallbackCleanup.current = cleanup;
    const fallback = /Android/i.test(navigator.userAgent) ? PLAY_STORE_URL : APP_STORE_URL;
    fallbackTimer.current = window.setTimeout(() => {
      cleanup();
      if (!leftBrowser && document.visibilityState === 'visible') window.location.assign(fallback);
    }, 2800);
    window.location.assign(appLink);
  }, [appLink]);

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `Join ${community?.name || 'this community'} on Snows ProAm`, url: window.location.href });
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      setCopyStatus('Link copied');
      window.setTimeout(() => setCopyStatus(''), 1800);
    } catch (shareError) {
      if (shareError?.name === 'AbortError') return;
      setCopyStatus('Share unavailable');
    }
  };

  return <main className="community-invite-page">
    <svg className="community-invite-track" viewBox="0 0 1200 1200" fill="none" aria-hidden="true">
      {[0, 1, 2, 3, 4, 5].map(index => <rect key={index} x={180 + index * 32} y={-160 + index * 32} width={820 - index * 64} height={1340 - index * 64} rx={410 - index * 32} stroke="currentColor" strokeWidth="3" />)}
      <path d="M80 520h160l65-110 80 220 75-160h120" stroke="currentColor" strokeWidth="7" />
    </svg>
    <header className="community-invite-nav">
      <Link className="community-invite-brand" to="/" aria-label="Snows ProAm home"><img src={logo} alt="" /></Link>
      <span className="community-invite-tagline">WHERE ATHLETES BELONG</span>
      <Link className="community-invite-nav-link" to="/communities"><span className="community-invite-nav-label">Explore communities</span><span aria-hidden="true">↗</span></Link>
    </header>
    <div className="community-invite-shell">
      {loading ? <section className="community-invite-state" role="status">Loading community…</section> : error ? (
        <section className="community-invite-state" role="alert"><p className="community-invite-eyebrow">COMMUNITY INVITE</p><h2>Community unavailable</h2><p>{error}</p><button onClick={() => setAttempt(value => value + 1)}>Try again</button><Link to="/">Back to Snows ProAm</Link></section>
      ) : community ? <article className="community-invite-card" aria-label={`${community.name} community invitation`}>
        <div className="community-invite-banner"><strong>SNOWS <b>PROAM</b></strong><span>COMMUNITY INVITE</span></div>
        <div className="community-invite-body">
          <div className="community-invite-identity">
            <div className="community-invite-avatar"><div>{community.image_url ? <img src={community.image_url} alt={`${community.name} logo`} /> : <Users aria-hidden="true" />}</div></div>
            <div className="community-invite-identity-copy"><p className="community-invite-eyebrow">YOU’RE INVITED TO JOIN</p><h2>{community.name}</h2><p>A Snows ProAm community</p></div>
          </div>
          <div className="community-invite-meta" aria-label="Community details">
            <div><small>Access</small><span className="community-invite-meta-value"><span className="community-invite-meta-icon" aria-hidden="true">{isPrivate ? '🔒' : '🌐'}</span><strong>{isPrivate ? 'Private' : 'Public'}</strong></span></div>
            <div className="community-invite-meta-size"><small>Community size</small><span className="community-invite-meta-value"><span className="community-invite-meta-icon" aria-hidden="true">👥</span><strong><b>{Number(community.member_count || 0)}</b> {Number(community.member_count || 0) === 1 ? 'member' : 'members'}</strong></span></div>
            {community.country && <div className="community-invite-meta-country"><small>Based in</small><span className="community-invite-meta-value"><span className="community-invite-meta-icon" aria-hidden="true">{countryFlag(community.country)}</span><strong>{community.country}</strong></span></div>}
            {community.sport_id && <div><small>Sport</small><span className="community-invite-meta-value"><span className="community-invite-meta-icon" aria-hidden="true">{sportEmoji(community.sport_id)}</span><strong>{community.sport_id}</strong></span></div>}
          </div>
          <section className="community-invite-overview" aria-labelledby="community-about"><h3 id="community-about">About this community</h3><p>{community.description || 'A place to connect, move and make progress together.'}</p></section>
          <section className="community-invite-features" aria-labelledby="community-features"><div><h3 id="community-features">Move together on Snows ProAm</h3><span>BUILT FOR COMMUNITY</span></div><div className="community-invite-feature-grid"><div><MessageCircle aria-hidden="true" /><b>Chat</b><small>Stay connected in one shared space.</small></div><div><Users aria-hidden="true" /><b>Groups</b><small>Create circles for every interest.</small></div><div><Activity aria-hidden="true" /><b>Sessions</b><small>Plan activities and meetups together.</small></div><div><Trophy aria-hidden="true" /><b>Challenges</b><small>Build momentum with shared goals.</small></div></div></section>
          <div className="community-invite-connect"><div><h3>Join {community.name}</h3><p>Continue in Snows ProAm to {isPrivate ? 'request access' : 'join the community'}.</p></div><a className="community-invite-primary" href={appLink} onClick={openApp}>Continue in app <ArrowUpRight aria-hidden="true" /></a></div>
          <section className="community-invite-download" aria-label="Download Snows ProAm"><p>GET THE SNOWS PROAM APP</p><h3>Take this community with you.</h3><div className="community-invite-store-links"><a href={APP_STORE_URL}><FontAwesomeIcon icon={faApple} aria-hidden="true" /><span><small>Download for</small><strong>iPhone</strong></span></a><a href={PLAY_STORE_URL}><FontAwesomeIcon icon={faAndroid} aria-hidden="true" /><span><small>Download for</small><strong>Android</strong></span></a></div><div className="community-invite-utility"><button type="button" onClick={shareLink} aria-label={`Share ${community.name}`}><Share2 aria-hidden="true" />{copyStatus === 'Link copied' ? 'Copied' : 'Share'}</button><a className="community-invite-socials" href={DOWNLOAD_URL} aria-label="Snows ProAm socials"><Instagram aria-hidden="true" /><span>Socials</span><ArrowUpRight aria-hidden="true" /></a></div><span className="community-invite-copy-status" aria-live="polite">{copyStatus}</span></section>
        </div>
      </article> : null}
      <footer className="community-invite-footer"><strong>EVERY ATHLETE. EVERY LEVEL.</strong><p>Community. Support. Progress.</p><Link to="/">Discover Snows ProAm ↗</Link></footer>
    </div>
  </main>;
}
